import React, { createContext, useContext, useState, useEffect, useRef } from 'react';
import { currentUser as mockCurrentUser, products as mockProducts, notifications as mockNotifications, messages as mockMessages, conversations as mockConversations, orders as mockOrders, offers as mockOffers, users as mockUsers, categories as mockCategories, walletTransactions as mockWalletTransactions, referralData as mockReferralData } from '../data/mockData';
import { setExternalUserId, removeExternalUserId, setUserTags } from '../services/onesignal';
import { authAPI, productsAPI, categoriesAPI, ordersAPI, walletAPI, messagesAPI, offersAPI, notificationsAPI } from '../services/api';
import { toast } from 'sonner';

const AppContext = createContext();

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Flag to control API vs Mock mode
const USE_API = true; // Set to true to use real API, false for mock data

export const useApp = () => {
  const context = useContext(AppContext);
  if (!context) {
    throw new Error('useApp must be used within an AppProvider');
  }
  return context;
};

export const AppProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  // Initialize products as empty - will be loaded from API
  const [products, setProducts] = useState([]);
  const [notifications, setNotifications] = useState([]);
  const [messages, setMessages] = useState([]);
  const [conversations, setConversations] = useState([]);
  const [orders, setOrders] = useState([]);
  const [offers, setOffers] = useState([]);
  const [users, setUsers] = useState(mockUsers);
  const [categories, setCategories] = useState(() => {
    try {
      const cached = localStorage.getItem('mintedCategoriesCache');
      return cached ? JSON.parse(cached) : [];
    } catch { return []; }
  });
  const [cart, setCart] = useState([]);
  const [loading, setLoading] = useState(true);
  const [walletTransactions, setWalletTransactions] = useState([]);
  const [referralData] = useState(mockReferralData);
  const [apiError, setApiError] = useState(null);
  const [unreadMessagesCount, setUnreadMessagesCount] = useState(0);

  // Load initial data
  useEffect(() => {
    loadInitialData();
  }, []);

  // Check for stored auth on mount
  useEffect(() => {
    const storedUser = localStorage.getItem('mintedUser');
    const storedToken = localStorage.getItem('mintedToken');
    if (storedUser) {
      const parsedUser = JSON.parse(storedUser);
      if (!parsedUser.wallet) {
        parsedUser.wallet = {
          availableBalance: 0,
          pendingBalance: 0,
          totalEarnings: 0
        };
      }
      setUser(parsedUser);
      setIsAuthenticated(true);
      
      // Load user-specific data (always load if USE_API is true, token is optional)
      if (USE_API) {
        loadUserData(parsedUser.id);
      }
    }
  }, []);

  // Load initial data from API or mock
  const loadInitialData = async () => {
    try {
      if (USE_API) {
        const [productsData, categoriesData] = await Promise.all([
          productsAPI.getAll().catch((err) => {
            console.warn('Products API failed:', err);
            return null;
          }),
          categoriesAPI.getAll().catch((err) => {
            console.warn('Categories API failed:', err);
            return null;
          })
        ]);
        
        if (productsData && productsData.length > 0) {
          setProducts(productsData);
        }
        if (categoriesData && categoriesData.length > 0) {
          setCategories(categoriesData);
          try { localStorage.setItem('mintedCategoriesCache', JSON.stringify(categoriesData)); } catch {}
        }
      }
    } catch (error) {
      console.error('Error loading initial data:', error);
    } finally {
      setLoading(false);
    }
  };

  // Force refresh products from API (call after admin actions)
  const refreshProducts = async () => {
    try {
      const productsData = await productsAPI.getAll().catch(() => null);
      if (productsData && productsData.length > 0) {
        setProducts(productsData);
      }
    } catch (error) {
      console.error('Error refreshing products:', error);
    }
  };

  // Auto-refresh products when tab becomes visible or every 60s
  useEffect(() => {
    const handleVisibility = () => {
      if (document.visibilityState === 'visible') {
        refreshProducts();
      }
    };
    document.addEventListener('visibilitychange', handleVisibility);

    const interval = setInterval(() => {
      if (document.visibilityState === 'visible') {
        refreshProducts();
      }
    }, 60000);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibility);
      clearInterval(interval);
    };
  }, []);

  // Load user-specific data after login
  // Normalize order fields from snake_case (backend) to camelCase (frontend)
  const normalizeOrder = (o) => ({
    ...o,
    buyerId: o.buyer_id || o.buyerId,
    sellerId: o.seller_id || o.sellerId,
    productId: o.product_id || o.productId,
    totalPrice: o.total_price || o.totalPrice || 0,
    shippingMethod: o.shipping_method || o.shippingMethod,
    trackingNumber: o.tracking_number || o.trackingNumber,
    trackingUrl: o.tracking_url || o.trackingUrl,
    createdAt: o.created_at || o.createdAt,
    shippedAt: o.shipped_at || o.shippedAt,
    deliveredAt: o.delivered_at || o.deliveredAt,
    fundsStatus: o.funds_status || o.fundsStatus,
    fundsReleaseDate: o.funds_release_date || o.fundsReleaseDate,
    paymentIntentId: o.payment_intent_id || o.paymentIntentId,
    productTitle: o.product_title || o.productTitle,
    productImage: o.product_image || o.productImage,
    productCondition: o.product_condition || o.productCondition,
    productSize: o.product_size || o.productSize,
    buyerName: o.buyer_name || o.buyerName,
    sellerName: o.seller_name || o.sellerName,
  });

  const loadUserData = async (userId) => {
    // Sync profile fields from the server so edits made on other devices stick
    fetch(`${API_URL}/api/auth/user/${userId}`)
      .then(res => res.ok ? res.json() : null)
      .then(fresh => {
        if (!fresh) return;
        setUser(prev => {
          if (!prev) return prev;
          const merged = {
            ...prev,
            name: fresh.name ?? prev.name,
            bio: fresh.bio ?? prev.bio,
            location: fresh.location ?? prev.location,
            avatar: fresh.avatar ?? prev.avatar,
            verified: fresh.verified ?? prev.verified,
            is_store: fresh.is_store ?? prev.is_store,
            store_name: fresh.store_name ?? prev.store_name,
            store_description: fresh.store_description ?? prev.store_description,
          };
          try { localStorage.setItem('mintedUser', JSON.stringify(merged)); } catch {}
          return merged;
        });
      })
      .catch(() => {});
    try {
      // Load ALL user data in PARALLEL for faster loading
      const [notificationsData, ordersData, offersData, walletData, conversationsData] = await Promise.all([
        notificationsAPI.getAll(userId).catch(() => null),
        ordersAPI.getByUser(userId).catch(() => null),
        offersAPI.getByUser(userId).catch(() => null),
        walletAPI.getTransactions(userId).catch(() => null),
        messagesAPI.getConversations(userId).catch(() => null)
      ]);
      
      setNotifications(notificationsData || []);
      setOrders((ordersData || []).map(normalizeOrder));
      setOffers(offersData || mockOffers);
      setWalletTransactions(walletData || mockWalletTransactions);
      setConversations(conversationsData || mockConversations);
    } catch (error) {
      console.error('Error loading user data:', error);
      setNotifications([]);
      setOrders([]);
      setOffers(mockOffers);
      setWalletTransactions(mockWalletTransactions);
    }
  };

  // Auth functions
  const login = async (email, password) => {
    setLoading(true);
    try {
      if (USE_API) {
        const response = await authAPI.login(email, password);
        if (response.user) {
          // Transform snake_case to camelCase for wallet
          const loggedInUser = {
            ...response.user,
            reviewCount: response.user.review_count,
            joinedDate: response.user.joined_date,
            salesCount: response.user.sales_count,
            notificationPreferences: response.user.notification_preferences,
            wallet: response.user.wallet ? {
              availableBalance: response.user.wallet.available_balance || 0,
              pendingBalance: response.user.wallet.pending_balance || 0,
              totalEarnings: response.user.wallet.total_earnings || 0
            } : { availableBalance: 0, pendingBalance: 0, totalEarnings: 0 }
          };
          
          setUser(loggedInUser);
          setIsAuthenticated(true);
          localStorage.setItem('mintedUser', JSON.stringify(loggedInUser));
          if (response.token) {
            localStorage.setItem('mintedToken', response.token);
          }
          
          // Load user data
          await loadUserData(loggedInUser.id);
          
          // OneSignal
          try {
            setExternalUserId(String(loggedInUser.id)).catch(() => {});
            setUserTags({ role: loggedInUser.role, location: loggedInUser.location }).catch(() => {});
          } catch (e) {}
          
          setLoading(false);
          return { success: true };
        }
      }
    } catch (error) {
      console.log('API login failed, trying mock:', error.message);
    }
    
    // Fallback to mock login
    const foundUser = mockUsers.find(u => u.email === email);
    if (foundUser || email === mockCurrentUser.email) {
      const loggedInUser = { ...(foundUser || mockCurrentUser) };
      if (!loggedInUser.wallet) {
        loggedInUser.wallet = mockCurrentUser.wallet || { availableBalance: 0, pendingBalance: 0, totalEarnings: 0 };
      }
      setUser(loggedInUser);
      setIsAuthenticated(true);
      localStorage.setItem('mintedUser', JSON.stringify(loggedInUser));
      
      // Initialize with empty data (will be loaded from API)
      setNotifications([]);
      setOrders(mockOrders);
      setOffers(mockOffers);
      setWalletTransactions(mockWalletTransactions);
      
      try {
        setExternalUserId(String(loggedInUser.id)).catch(() => {});
        setUserTags({ role: loggedInUser.role, location: loggedInUser.location }).catch(() => {});
      } catch (e) {}
      
      setLoading(false);
      return { success: true };
    }
    
    setLoading(false);
    return { success: false, error: 'Invalid credentials' };
  };

  const register = async (userData) => {
    setLoading(true);
    try {
      if (USE_API) {
        const response = await authAPI.register(userData);
        if (response.user) {
          const newUser = response.user;
          setUser(newUser);
          setIsAuthenticated(true);
          localStorage.setItem('mintedUser', JSON.stringify(newUser));
          if (response.token) {
            localStorage.setItem('mintedToken', response.token);
          }
          
          try {
            setExternalUserId(String(newUser.id)).catch(() => {});
            setUserTags({ role: newUser.role, location: newUser.location || 'Australia' }).catch(() => {});
          } catch (e) {}
          
          setLoading(false);
          return { success: true };
        }
      }
    } catch (error) {
      setLoading(false);
      console.log('API register failed:', error.message);
      
      // Return specific error message
      let errorMessage = 'Registration failed. Please try again.';
      if (error.message && error.message.includes('already registered')) {
        errorMessage = 'This email is already registered. Please use a different email or try logging in.';
      } else if (error.message && error.message.includes('Email already')) {
        errorMessage = 'This email is already registered. Please use a different email or try logging in.';
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      return { success: false, error: errorMessage };
    }
    
    // Fallback to mock registration
    const newUser = {
      id: users.length + 1,
      ...userData,
      avatar: `https://ui-avatars.com/api/?name=${encodeURIComponent(userData.name)}&background=00c978&color=fff`,
      rating: 0,
      reviewCount: 0,
      joinedDate: new Date().toISOString().split('T')[0],
      role: 'buyer',
      salesCount: 0,
      verified: false,
      notificationPreferences: { email: true, push: true, inApp: true },
      wallet: { availableBalance: 0, pendingBalance: 0, totalEarnings: 0 }
    };
    setUsers([...users, newUser]);
    setUser(newUser);
    setIsAuthenticated(true);
    localStorage.setItem('mintedUser', JSON.stringify(newUser));
    
    try {
      setExternalUserId(String(newUser.id)).catch(() => {});
      setUserTags({ role: newUser.role, location: newUser.location || 'Australia' }).catch(() => {});
    } catch (e) {}
    
    setLoading(false);
    return { success: true };
  };

  const logout = () => {
    try {
      removeExternalUserId().catch(() => {});
    } catch (e) {}
    
    setUser(null);
    setIsAuthenticated(false);
    setNotifications([]);
    setOrders([]);
    setOffers([]);
    setWalletTransactions([]);
    localStorage.removeItem('mintedUser');
    localStorage.removeItem('mintedToken');
  };

  const switchRole = () => {
    if (user) {
      const newRole = user.role === 'buyer' ? 'seller' : 'buyer';
      const updatedUser = { ...user, role: newRole };
      setUser(updatedUser);
      localStorage.setItem('mintedUser', JSON.stringify(updatedUser));
    }
  };

  // Product functions
  const addProduct = async (product) => {
    if (USE_API && user) {
      try {
        const newProduct = await productsAPI.create(product, user.id);
        if (newProduct && newProduct.id) {
          setProducts([newProduct, ...products]);
          console.log('✅ Product saved to server:', newProduct.id);
          return newProduct;
        } else {
          console.error('❌ Server returned invalid product');
          throw new Error('Server returned invalid product');
        }
      } catch (error) {
        console.error('❌ API addProduct failed:', error);
        throw error; // Re-throw so the UI can show error
      }
    }
    
    // Only use mock if API is disabled (development mode)
    if (!USE_API) {
      const newProduct = {
        id: `mock-${Date.now()}`,
        ...product,
        seller_id: user?.id,
        sellerId: user?.id,
        createdAt: new Date().toISOString().split('T')[0],
        views: 0,
        likes: 0
      };
      setProducts([newProduct, ...products]);
      return newProduct;
    }
    
    throw new Error('Not authenticated');
  };

  const updateProduct = async (productId, updates) => {
    try {
      if (USE_API) {
        await productsAPI.update(productId, updates);
      }
    } catch (error) {
      console.error('API updateProduct failed:', error);
    }
    setProducts(products.map(p => p.id === productId ? { ...p, ...updates } : p));
  };

  const deleteProduct = async (productId) => {
    try {
      if (USE_API) {
        await productsAPI.delete(productId);
      }
    } catch (error) {
      console.error('API deleteProduct failed:', error);
      return false;
    }
    setProducts(products.filter(p => p.id !== productId));
    return true;
  };

  // Get products by category
  const getProductsByCategory = (categoryName) => {
    return products.filter(p => p.category === categoryName && !p.sold);
  };

  // Notification functions
  const markNotificationRead = async (notificationId) => {
    setNotifications(notifications.map(n => 
      n.id === notificationId ? { ...n, read: true } : n
    ));
    try {
      await fetch(`${API_URL}/api/notifications/${notificationId}/read`, { method: 'PUT' });
    } catch (err) { console.error('Failed to mark notification read:', err); }
  };

  const markAllNotificationsRead = async () => {
    setNotifications(notifications.map(n => ({ ...n, read: true })));
    if (user?.id) {
      try {
        await fetch(`${API_URL}/api/notifications/user/${user.id}/read-all`, { method: 'PUT' });
      } catch (err) { console.error('Failed to mark all read:', err); }
    }
  };

  const addNotification = (notification) => {
    const newNotification = {
      id: String(Date.now()),
      user_id: user?.id,
      read: false,
      created_at: new Date().toISOString(),
      ...notification
    };
    setNotifications([newNotification, ...notifications]);
  };

  const unreadNotificationsCount = notifications.filter(n => !n.read && (n.user_id === user?.id || n.userId === user?.id)).length;

  // Message functions
  const sendMessage = async (conversationId, text, receiverId, productId) => {
    if (!user?.id) return null;
    
    try {
      if (USE_API) {
        // Send message via API
        const messageData = {
          receiver_id: receiverId,
          product_id: productId,
          text: text
        };
        const newMessage = await messagesAPI.send(messageData, user.id);
        
        // Update local state (dedupe - the WS push may have already delivered it)
        setMessages(prev => prev.some(m => m.id === newMessage.id) ? prev : [...prev, newMessage]);
        
        // Update conversation's last message
        setConversations(prev => prev.map(c => 
          c.id === conversationId 
            ? { ...c, last_message: text, last_message_time: newMessage.timestamp }
            : c
        ));
        
        return newMessage;
      } else {
        // Fallback to local-only (mock mode)
        const newMessage = {
          id: messages.length + 1,
          conversationId,
          senderId: user?.id,
          receiverId,
          productId,
          text,
          timestamp: new Date().toISOString(),
          read: false
        };
        setMessages([...messages, newMessage]);
        
        setConversations(conversations.map(c => 
          c.id === conversationId 
            ? { ...c, lastMessage: text, lastMessageTime: newMessage.timestamp }
            : c
        ));
        
        return newMessage;
      }
    } catch (error) {
      console.error('Error sending message:', error);
      toast.error(error.message || 'Message could not be sent');
      return null;
    }
  };

  const startConversation = async (receiverId, productId, initialMessage) => {
    if (!user?.id) return null;
    
    // Check for existing conversation
    const existingConv = conversations.find(
      c => c.participants?.includes(user?.id) && c.participants?.includes(receiverId) && c.product_id === productId
    );
    
    if (existingConv) {
      await sendMessage(existingConv.id, initialMessage, receiverId, productId);
      return existingConv;
    }
    
    try {
      if (USE_API) {
        // Send first message - backend will create conversation automatically
        const messageData = {
          receiver_id: receiverId,
          product_id: productId,
          text: initialMessage
        };
        const newMessage = await messagesAPI.send(messageData, user.id);
        
        // Reload conversations to get the new one
        const conversationsData = await messagesAPI.getConversations(user.id);
        if (conversationsData) {
          setConversations(conversationsData);
        }
        
        // Find and return the new conversation
        const newConv = conversationsData?.find(c => c.product_id === productId && c.participants?.includes(receiverId));
        return newConv;
      } else {
        // Fallback to local-only
        const newConversation = {
          id: conversations.length + 1,
          participants: [user?.id, receiverId],
          productId,
          lastMessage: initialMessage,
          lastMessageTime: new Date().toISOString(),
          unreadCount: 0
        };
        setConversations([...conversations, newConversation]);
        sendMessage(newConversation.id, initialMessage, receiverId, productId);
        return newConversation;
      }
    } catch (error) {
      console.error('Error starting conversation:', error);
      return null;
    }
  };

  // Load messages for a specific conversation from API
  const loadConversationMessages = async (conversationId) => {
    if (!USE_API) return;
    
    try {
      const messagesData = await messagesAPI.getMessages(conversationId);
      if (messagesData) {
        // Merge with existing messages, avoiding duplicates
        setMessages(prev => {
          const existingIds = new Set(prev.map(m => m.id));
          const newMessages = messagesData.filter(m => !existingIds.has(m.id));
          return [...prev, ...newMessages];
        });
      }
    } catch (error) {
      console.error('Error loading conversation messages:', error);
    }
  };

  // Refresh conversations list
  const refreshConversations = async () => {
    if (!USE_API || !user?.id) return;
    
    try {
      const conversationsData = await messagesAPI.getConversations(user.id);
      if (conversationsData) {
        const unique = Array.from(new Map(conversationsData.map(c => [c.id, c])).values());
        setConversations(unique);
        // Calculate unread messages count
        const unreadCount = unique.reduce((total, conv) => {
          return total + (conv.unread_count || conv.unreadCount || 0);
        }, 0);
        setUnreadMessagesCount(unreadCount);
      }
    } catch (error) {
      console.error('Error refreshing conversations:', error);
    }
  };

  // ==================== REALTIME CHAT (WEBSOCKET) ====================
  const [typingConversations, setTypingConversations] = useState({});
  const wsRef = useRef(null);

  useEffect(() => {
    if (!user?.id) return;
    let closed = false;
    let ws;
    const connect = () => {
      try {
        const wsUrl = API_URL.replace(/^http/, 'ws') + `/api/ws/${user.id}`;
        ws = new WebSocket(wsUrl);
        wsRef.current = ws;
        ws.onmessage = (e) => {
          try {
            const data = JSON.parse(e.data);
            if (data.type === 'new_message') {
              const msg = data.message;
              setMessages(prev => prev.some(m => m.id === msg.id) ? prev : [...prev, msg]);
              refreshConversations();
            } else if (data.type === 'typing') {
              setTypingConversations(prev => ({ ...prev, [data.conversation_id]: Date.now() }));
            }
          } catch {}
        };
        ws.onclose = () => { if (!closed) setTimeout(connect, 3000); };
        ws.onerror = () => { try { ws.close(); } catch {} };
      } catch {}
    };
    connect();
    return () => { closed = true; try { ws?.close(); } catch {} };
  }, [user?.id]);

  const sendTypingSignal = (conversationId, receiverId) => {
    if (wsRef.current?.readyState === 1) {
      wsRef.current.send(JSON.stringify({ type: 'typing', conversation_id: conversationId, to: receiverId }));
    }
  };

  // Order functions
  const createOrder = (orderData) => {
    const newOrder = {
      id: orders.length + 1,
      buyerId: user?.id,
      status: orderData.shippingMethod === 'Pickup' ? 'pickup_arranged' : 'pending',
      trackingNumber: null,
      trackingUrl: null,
      createdAt: new Date().toISOString().split('T')[0],
      shippedAt: null,
      deliveredAt: null,
      fundsStatus: 'held', // held, pending_release, released
      ...orderData
    };
    setOrders([...orders, newOrder]);
    
    // Add pending balance to seller's wallet
    const product = products.find(p => p.id === orderData.productId);
    if (product) {
      // Create wallet transaction
      const newTransaction = {
        id: walletTransactions.length + 1,
        userId: product.seller_id || product.sellerId,
        type: 'sale',
        amount: orderData.totalPrice,
        status: 'pending',
        orderId: newOrder.id,
        description: `Sale: ${product.title}`,
        createdAt: new Date().toISOString(),
        releaseDate: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString()
      };
      setWalletTransactions([newTransaction, ...walletTransactions]);
    }
    
    updateProduct(orderData.productId, { sold: true });
    
    return newOrder;
  };

  const updateOrderStatus = (orderId, status, additionalData = {}) => {
    setOrders(orders.map(o => 
      o.id === orderId ? { ...o, status, ...additionalData } : o
    ));
  };

  const addTrackingInfo = async (orderId, trackingNumber, trackingUrl, shippingMethod) => {
    try {
      if (USE_API) {
        const res = await ordersAPI.addTracking(orderId, trackingNumber, trackingUrl, shippingMethod);
        if (!res.success) throw new Error('Failed to add tracking');
      }
      // Update local state
      setOrders(orders.map(o => 
        o.id === orderId 
          ? { ...o, trackingNumber, trackingUrl, shippingMethod, status: 'shipped', shippedAt: new Date().toISOString() }
          : o
      ));
      return { success: true };
    } catch (error) {
      console.error('Error adding tracking:', error);
      return { success: false, error: error.message };
    }
  };

  const confirmDelivery = async (orderId) => {
    try {
      if (USE_API) {
        const res = await ordersAPI.confirmDelivery(orderId);
        if (!res.success) throw new Error(res.message || 'Failed to confirm delivery');
      }
      const order = orders.find(o => o.id === orderId);
      setOrders(orders.map(o => 
        o.id === orderId 
          ? { ...o, status: 'completed', deliveredAt: new Date().toISOString(), fundsStatus: 'released', buyer_confirmed: true }
          : o
      ));
      // Update local wallet state
      if (order) {
        setWalletTransactions(walletTransactions.map(t => 
          t.orderId === orderId 
            ? { ...t, status: 'available', completedAt: new Date().toISOString() }
            : t
        ));
      }
      return { success: true };
    } catch (error) {
      console.error('Error confirming delivery:', error);
      return { success: false, error: error.message };
    }
  };

  const confirmPickup = async (orderId) => {
    try {
      if (USE_API) {
        const res = await ordersAPI.confirmPickup(orderId);
        if (!res.success) throw new Error(res.message || 'Failed to confirm pickup');
      }
      setOrders(orders.map(o => 
        o.id === orderId 
          ? { ...o, status: 'completed', deliveredAt: new Date().toISOString(), fundsStatus: 'released' }
          : o
      ));
      return { success: true };
    } catch (error) {
      console.error('Error confirming pickup:', error);
      return { success: false, error: error.message };
    }
  };

  // Wallet functions
  const requestWithdrawal = (amount, bankDetails) => {
    if (!user?.wallet || amount > user.wallet.availableBalance) {
      return { success: false, error: 'Insufficient funds' };
    }
    
    const newTransaction = {
      id: walletTransactions.length + 1,
      userId: user.id,
      type: 'withdrawal',
      amount: -amount,
      status: 'processing',
      orderId: null,
      description: `Bank Transfer to ${bankDetails.bankName} **** ${bankDetails.accountNumber.slice(-4)}`,
      createdAt: new Date().toISOString(),
      estimatedArrival: new Date(Date.now() + 2 * 24 * 60 * 60 * 1000).toISOString()
    };
    setWalletTransactions([newTransaction, ...walletTransactions]);
    
    const updatedUser = {
      ...user,
      wallet: {
        ...user.wallet,
        availableBalance: user.wallet.availableBalance - amount
      }
    };
    setUser(updatedUser);
    localStorage.setItem('mintedUser', JSON.stringify(updatedUser));
    
    return { success: true, transaction: newTransaction };
  };

  const getUserWalletTransactions = () => {
    if (!user) return [];
    return walletTransactions.filter(t => t.userId === user.id);
  };

  // Offer functions
  const makeOffer = (productId, offerPrice, message) => {
    const product = products.find(p => p.id === productId);
    const newOffer = {
      id: offers.length + 1,
      productId,
      buyerId: user?.id,
      sellerId: product?.sellerId,
      offerPrice,
      originalPrice: product?.price,
      message,
      status: 'pending',
      createdAt: new Date().toISOString()
    };
    setOffers([...offers, newOffer]);
    return newOffer;
  };

  const respondToOffer = (offerId, status) => {
    setOffers(offers.map(o => 
      o.id === offerId ? { ...o, status } : o
    ));
  };

  // Update user preferences
  const updateNotificationPreferences = (preferences) => {
    if (user) {
      const updatedUser = {
        ...user,
        notificationPreferences: { ...user.notificationPreferences, ...preferences }
      };
      setUser(updatedUser);
      localStorage.setItem('mintedUser', JSON.stringify(updatedUser));
    }
  };

  const updateProfile = (updates) => {
    if (user) {
      const updatedUser = { ...user, ...updates };
      setUser(updatedUser);
      localStorage.setItem('mintedUser', JSON.stringify(updatedUser));
    }
  };

  // Get user by ID
  // Synchronous version - returns cached user or placeholder
  const getUserByIdSync = (userId) => {
    // First check local users
    const localUser = users.find(u => u.id === userId);
    if (localUser) return localUser;
    
    // Check if it's a demo seller - return placeholder
    if (userId?.startsWith('demo_seller')) {
      return {
        id: userId,
        name: 'Minted Seller',
        avatar: 'https://ui-avatars.com/api/?name=Minted+Seller&background=00c978&color=fff',
        rating: 4.8,
        location: 'Australia',
        is_placeholder: true
      };
    }
    
    return {
      id: userId,
      name: 'User',
      avatar: `https://ui-avatars.com/api/?name=User&background=00c978&color=fff`,
      rating: 4.5,
      location: 'Australia',
      is_placeholder: true
    };
  };

  // Async version - fetches from API if not cached
  const getUserById = async (userId) => {
    // First check local users
    const localUser = users.find(u => u.id === userId);
    if (localUser) return localUser;
    
    // Check if it's a demo seller - return placeholder
    if (userId?.startsWith('demo_seller')) {
      return {
        id: userId,
        name: 'Minted Seller',
        avatar: 'https://ui-avatars.com/api/?name=Minted+Seller&background=00c978&color=fff',
        rating: 4.8,
        location: 'Australia'
      };
    }
    
    // Try to fetch from API
    if (USE_API && userId) {
      try {
        const userData = await authAPI.getUser(userId);
        if (userData) {
          // Add to local users
          setUsers(prev => [...prev, userData]);
          return userData;
        }
      } catch (error) {
        console.error('Error fetching user:', error);
      }
    }
    
    return {
      id: userId,
      name: 'User',
      avatar: `https://ui-avatars.com/api/?name=User&background=00c978&color=fff`,
      rating: 4.5,
      location: 'Australia'
    };
  };

  // Get products by seller
  const getProductsBySeller = (sellerId) => {
    return products.filter(p => 
      (p.seller_id === sellerId || p.sellerId === sellerId) && !p.sold
    );
  };

  // Get user's orders (as buyer or seller)
  const getUserOrders = (asSeller = false) => {
    if (!user) return [];
    return orders.filter(o => asSeller ? o.sellerId === user.id : o.buyerId === user.id);
  };

  // Like/unlike product
  const toggleLike = async (productId) => {
    if (!user?.id) {
      toast.error('Sign in to save items to your wishlist');
      return;
    }
    try {
      const res = await fetch(`${API_URL}/api/products/${productId}/like?user_id=${user.id}`, { method: 'POST' });
      if (res.ok) {
        const data = await res.json();
        setProducts(products.map(p => 
          p.id === productId ? { ...p, likes: data.likes, liked_by: data.liked ? [...(p.liked_by || []), user.id] : (p.liked_by || []).filter(id => id !== user.id) } : p
        ));
      }
    } catch (err) {
      console.error('Failed to toggle like:', err);
    }
  };

  const value = {
    // State
    user,
    isAuthenticated,
    products,
    notifications,
    messages,
    conversations,
    orders,
    offers,
    users,
    categories,
    cart,
    loading,
    unreadNotificationsCount,
    walletTransactions,
    referralData,
    
    // Auth
    login,
    register,
    logout,
    switchRole,
    
    // Products
    addProduct,
    updateProduct,
    deleteProduct,
    getProductsBySeller,
    getProductsByCategory,
    toggleLike,
    
    // Notifications
    markNotificationRead,
    markAllNotificationsRead,
    addNotification,
    
    // Messages
    sendMessage,
    typingConversations,
    sendTypingSignal,
    startConversation,
    loadConversationMessages,
    refreshConversations,
    unreadMessagesCount,
    
    // Orders
    createOrder,
    updateOrderStatus,
    addTrackingInfo,
    confirmDelivery,
    confirmPickup,
    getUserOrders,
    
    // Wallet
    requestWithdrawal,
    getUserWalletTransactions,
    
    // Offers
    makeOffer,
    respondToOffer,
    
    // User
    updateNotificationPreferences,
    updateProfile,
    getUserById,
    getUserByIdSync,
    refreshUserData: loadUserData,
    refreshProducts,
    
    // Cart
    setCart
  };

  return (
    <AppContext.Provider value={value}>
      {children}
    </AppContext.Provider>
  );
};

export default AppContext;
